import type {
  BetslipActions,
  BetslipState,
  BetslipStore,
  SliceCreator,
} from "@/features/betslip/stores/types";
import { v4 as uuidv4 } from "uuid";
import {
  clearAcceptedForEdit,
  exitConfirmingIfMutated,
  resetCombo,
} from "@/features/betslip/stores/slices/submission";
import { safeExoticStake } from "@/utilities/exoticStake";
import type { BetSelection } from "@/features/betslip/types";
import {
  selectMultiLegs,
  selectCombinationsForLegCount,
} from "@/features/betslip/stores/computed";

export const initialStakesState: Pick<BetslipStore, "stakes"> = {
  stakes: {
    singles: {},
    multi: 0,
    combinations: {},
  },
};

const sameLegSet = (a: BetSelection[], b: BetSelection[]): boolean => {
  if (a.length !== b.length) return false;
  const ids = new Set(a.map((leg) => leg.outcomeId));
  return b.every((leg) => ids.has(leg.outcomeId));
};

// Combination stakes are entered against a specific leg set; once that set
// changes their totals no longer divide across the new counts, so the whole
// map goes. A change that leaves the legs alone keeps them.
//
// Placed and rejected combos go too. The card looks results up by size, so a
// record outliving its legs would show a placed bet over legs it was never
// placed on, and re-arm its stake for the next submit.
export const pruneComboStakes = (
  state: BetslipStore,
  next: BetslipStore,
): Pick<BetslipStore, "stakes"> => {
  if (sameLegSet(selectMultiLegs(state), selectMultiLegs(next))) {
    return { stakes: state.stakes };
  }
  return { stakes: { ...state.stakes, combinations: {} } };
};

export const pruneStakesAfterRemoval = (
  state: BetslipStore,
  drop: ReadonlySet<string> | null,
  next: BetslipStore,
): Pick<BetslipStore, "stakes"> => {
  let nextSingles = state.stakes.singles;
  if (drop) {
    nextSingles = {};
    for (const [outcomeId, amount] of Object.entries(state.stakes.singles)) {
      if (!drop.has(outcomeId)) nextSingles[outcomeId] = amount;
    }
  }

  const { stakes: comboPruned } = pruneComboStakes(state, next);

  return {
    stakes: {
      ...comboPruned,
      singles: nextSingles,
      // Raw picks, not effective legs: an excluded event comes back, so its
      // stake parks. Below two picks there's nothing to come back to.
      multi: next.multiActiveLegs.length >= 2 ? state.stakes.multi : 0,
    },
  };
};

const computePromoUsed = (state: BetslipState): number => {
  let used = 0;
  for (const [outcomeId, isUsing] of Object.entries(state.promoCash.singles)) {
    if (isUsing) used += state.stakes.singles[outcomeId] ?? 0;
  }
  // A combo the leg count can't form is never submitted, so it holds no promo
  // cash. A parked multi keeps its claim: the card still shows the toggle, so
  // releasing it would let a single spend the same cash and both over-commit.
  if (state.promoCash.multi) used += state.stakes.multi;
  for (const combo of Object.values(selectCombinationsForLegCount(state))) {
    if (combo.isPromo) used += combo.total;
  }
  return used;
};

type StakesActions = Pick<
  BetslipActions,
  | "setSingleStake"
  | "setMultiStake"
  | "setStakeAll"
  | "setCombinationStake"
  | "setCombinationResult"
  | "dismissCombinationResult"
>;

export const stakesSlice: SliceCreator<
  typeof initialStakesState & StakesActions
> = (set) => ({
  ...initialStakesState,

  setSingleStake: (outcomeId, amount, promoBalance) => {
    set((state) => {
      const isUsingPromo = state.promoCash.singles[outcomeId] ?? false;
      const newPromoSingles = { ...state.promoCash.singles };

      if (isUsingPromo) {
        const promoUsed = computePromoUsed(state);
        const currentClaim = state.stakes.singles[outcomeId] ?? 0;
        const availableForThis = promoBalance - promoUsed + currentClaim;
        if (availableForThis < amount) {
          newPromoSingles[outcomeId] = false;
        }
      }

      const cleared = clearAcceptedForEdit(state);
      // Only the edited selection's rejection resets — other cards keep
      // their rejection alerts until they're touched.
      const nextResults = { ...cleared.selectionResults };
      if (nextResults[outcomeId]?.status === "rejected") {
        delete nextResults[outcomeId];
      }

      return {
        ...exitConfirmingIfMutated(state),
        ...cleared,
        selectionResults: nextResults,
        stakes: {
          ...cleared.stakes,
          singles: { ...cleared.stakes.singles, [outcomeId]: amount },
        },
        promoCash: { ...state.promoCash, singles: newPromoSingles },
      };
    });
  },

  setMultiStake: (amount, promoBalance) => {
    set((state) => {
      const isUsingPromo = state.promoCash.multi;
      let multiPromoOff = false;

      if (isUsingPromo) {
        const promoUsed = computePromoUsed(state);
        const currentClaim = state.stakes.multi;
        const availableForMulti = promoBalance - promoUsed + currentClaim;
        if (availableForMulti < amount) multiPromoOff = true;
      }

      // Restaking answers the multi's own rejection - its result and its
      // legs banners go. Accepted results still clear with their
      // stakes zeroed, so placed bets can't re-submit unless intentional.
      const cleared = clearAcceptedForEdit(state);

      return {
        ...exitConfirmingIfMutated(state),
        ...cleared,
        multiLegRejections: {},
        multiResult: null,
        stakes: { ...cleared.stakes, multi: amount },
        promoCash: multiPromoOff
          ? { ...state.promoCash, multi: false }
          : state.promoCash,
      };
    });
  },

  setStakeAll: (amount, promoBalance) => {
    set((state) => {
      const singles: Record<string, number> = {};
      const nextPromoSingles = { ...state.promoCash.singles };
      let runningPromoBalance = promoBalance;

      for (const [outcomeId, sel] of Object.entries(state.selections)) {
        const stake =
          sel.kind === "exotic"
            ? sel.combinations > 0
              ? Math.round(
                  safeExoticStake(amount / 100, sel.combinations) * 100,
                )
              : amount
            : amount;
        singles[outcomeId] = stake;

        if (nextPromoSingles[outcomeId]) {
          if (runningPromoBalance >= stake) {
            runningPromoBalance -= stake;
          } else {
            nextPromoSingles[outcomeId] = false;
          }
        }
      }

      return {
        ...exitConfirmingIfMutated(state),
        stakes: { ...state.stakes, singles },
        promoCash: { ...state.promoCash, singles: nextPromoSingles },
      };
    });
  },

  setCombinationStake: (size, stake) => {
    set((state) => {
      // Re-arms the slip, same as a single or the multi.
      const cleared = clearAcceptedForEdit(state);
      return {
        ...exitConfirmingIfMutated(state),
        ...cleared,
        stakes: {
          ...cleared.stakes,
          combinations: {
            ...cleared.stakes.combinations,
            // The correlationId ties the submitted bet back to this combo
            // when the response arrives; without one the response can't be
            // matched and the result is dropped. Keep a stable id across
            // restakes, like the legacy reducers.
            [size]: {
              ...stake,
              correlationId:
                stake.correlationId ||
                state.stakes.combinations[size]?.correlationId ||
                uuidv4(),
            },
          },
        },
      };
    });
  },

  // Like a single's dismiss: a placed combo's stake goes with its receipt so
  // the next submit can't re-send it, a rejected one keeps the stake it's
  // being fixed with, and no other card is touched.
  dismissCombinationResult: (size) => {
    set((state) => {
      if (state.submission.phase === "submitting") return state;
      const combo = state.stakes.combinations[size];
      if (!combo) return state;
      const dismissed =
        combo.betId !== undefined
          ? resetCombo(combo)
          : { ...combo, rejected: undefined };
      return {
        stakes: {
          ...state.stakes,
          combinations: { ...state.stakes.combinations, [size]: dismissed },
        },
      };
    });
  },

  setCombinationResult: (size, patch) => {
    set((state) => {
      const current = state.stakes.combinations[size];
      if (!current) return state;
      return {
        stakes: {
          ...state.stakes,
          combinations: {
            ...state.stakes.combinations,
            [size]: { ...current, ...patch },
          },
        },
      };
    });
  },
});
